import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Modal, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../utils/logger';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface InstallPWAButtonProps {
  style?: any;
  textStyle?: any;
}

const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({ style, textStyle }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detectar se é iOS
    const userAgent = navigator.userAgent.toLowerCase();
    const isiOS = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isiOS);

    // Detectar se já está instalado como PWA
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                            (window.navigator as any).standalone ||
                            document.referrer.includes('android-app://');
    setIsStandalone(isStandaloneMode);

    // Se já está instalado, não mostrar o botão
    if (isStandaloneMode) {
      return;
    }

    // Listener para Android (beforeinstallprompt)
    const handleBeforeInstallPrompt = (e: Event) => {
      logger.debug('PWA beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listener para quando o app é instalado
    const handleAppInstalled = () => {
      logger.info('PWA app installed successfully');
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Se já está instalado como PWA, não mostrar o botão
  if (isStandalone) {
    return null;
  }

  // Se é Android e não tem o prompt disponível, não mostrar
  if (!isIOS && !deferredPrompt) {
    return null;
  }

  const handleInstallClick = async () => {
    if (isIOS) {
      // Para iOS, mostrar modal com instruções
      setShowIOSModal(true);
    } else if (deferredPrompt) {
      // Para Android, usar o prompt nativo
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          logger.info('User accepted PWA installation');
        } else {
          logger.info('User rejected PWA installation');
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        logger.error('Error showing PWA installation prompt', { error });
        Alert.alert('Erro', 'Não foi possível instalar o aplicativo. Tente novamente.');
      }
    }
  };

  const IOSInstructionsModal = () => (
    <Modal
      visible={showIOSModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowIOSModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Instalar Aplicativo</Text>
            <TouchableOpacity
              onPress={() => setShowIOSModal(false)}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>
          
          <Text style={styles.modalSubtitle}>
            Para instalar o Poe no Racha em seu iPhone:
          </Text>
          
          <View style={styles.instructionStep}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <View style={styles.stepContent}>
              <Text style={styles.stepText}>
                Toque no ícone de <Text style={styles.boldText}>Compartilhar</Text> na barra do Safari
              </Text>
              <View style={styles.iconContainer}>
                <Ionicons name="share-outline" size={24} color="#007AFF" />
              </View>
            </View>
          </View>
          
          <View style={styles.instructionStep}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <View style={styles.stepContent}>
              <Text style={styles.stepText}>
                Role para baixo e selecione <Text style={styles.boldText}>Adicionar à Tela de Início</Text>
              </Text>
              <View style={styles.iconContainer}>
                <Ionicons name="add-circle-outline" size={24} color="#007AFF" />
              </View>
            </View>
          </View>
          
          <View style={styles.instructionStep}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <View style={styles.stepContent}>
              <Text style={styles.stepText}>
                Toque em <Text style={styles.boldText}>Adicionar</Text> para confirmar
              </Text>
            </View>
          </View>
          
          <TouchableOpacity
            style={styles.gotItButton}
            onPress={() => setShowIOSModal(false)}
          >
            <Text style={styles.gotItButtonText}>Entendi!</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  return (
    <>
      <TouchableOpacity
        style={[styles.installButton, style]}
        onPress={handleInstallClick}
        activeOpacity={0.8}
      >
        <Ionicons name="download-outline" size={20} color="#fff" style={styles.buttonIcon} />
        <Text style={[styles.buttonText, textStyle]}>
          {isIOS ? 'Instalar App' : 'Instalar App'}
        </Text>
      </TouchableOpacity>
      
      {isIOS && <IOSInstructionsModal />}
    </>
  );
};

const styles = StyleSheet.create({
  installButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#007AFF',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginVertical: 8,
  },
  buttonIcon: {
    marginRight: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 20,
    textAlign: 'center',
  },
  instructionStep: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  stepContent: {
    flex: 1,
    alignItems: 'flex-start',
  },
  stepText: {
    fontSize: 15,
    color: '#333',
    lineHeight: 20,
    marginBottom: 8,
  },
  boldText: {
    fontWeight: 'bold',
  },
  iconContainer: {
    alignSelf: 'flex-start',
  },
  gotItButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  gotItButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default InstallPWAButton;
